import React, { useState, useEffect } from "react";
import { Button, Input, Space, Tag, Typography, TreeSelect, message } from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { assetAPIWithErrorHandling, assetHelpers } from "../../../api/asset/asset";
import { organizationAPIWithErrorHandling } from "../../../api/organization/organization";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ListTableCard from "../../../components/common/ListTableCard";
import { confirmAction, confirmDelete } from "../../../components/common/confirmDialog";
import { actionColumnDef } from "../../../components/common/RowActions";
import ExportExcelButton from "../../../components/common/ExportExcelButton";
import ListToolbar, {
  selectVisibleColumns,
  useListToolbar,
} from "../../../components/common/ListToolbar";
import AssetFormDrawer from "./AssetFormDrawer";
import AssetDetailDrawer from "./AssetDetailDrawer";

const { Text } = Typography;

const DEFAULT_VISIBLE_FILTERS = [
  "search"
];
const DEFAULT_SORT = { sort_by: "created_at", sort_order: "desc" };
const DEFAULT_VISIBLE_COLUMNS = [
  "organization_name",
  "customer_name",
  "description",
  "representative_name",
  "representative_phone",
  "street",
  "status",
];

export default function AssetTable() {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    from: 0,
    to: 0,
    lastPage: 1,
  });
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    ...DEFAULT_SORT,
  });

  const [treeData, setTreeData] = useState([]);
  const [valueOrg, setValueOrg] = useState();

  // Tích chọn để xóa hàng loạt
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Thêm mới / chỉnh sửa / xem chi tiết đều làm bằng Drawer ngay trên danh sách
  const [formOpen, setFormOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const {
    visibleFilters, setVisibleFilters,
    visibleColumns, setVisibleColumns,
  } = useListToolbar({
    defaultVisibleFilters: DEFAULT_VISIBLE_FILTERS,
    defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
  });

  const flattenTreeData = (nodes, parentId = 0) => {
    let result = [];
    nodes.forEach((node) => {
      result.push({
        id: node.id,
        pId: parentId,
        value: String(node.id),
        title: node.name,
        isLeaf: node.children_recursive?.length === 0,
      });

      if (node.children_recursive && node.children_recursive.length > 0) {
        result = result.concat(flattenTreeData(node.children_recursive, node.id));
      }
    });
    return result;
  };

  const loadOrganizations = async () => {
    try {
      const response = await organizationAPIWithErrorHandling.getTreeStructure();
      if (response.success) {
        const rawData = response.data;
        const flatTree = flattenTreeData(rawData);
        setTreeData(flatTree);
      }
    } catch (error) {
      message.error(error.message);
    }
  };

  // Fetch data using the proper API
  // `sort` phải truyền được từ ngoài vào: setFilters chỉ có hiệu lực ở lần render sau,
  // nên nếu đọc filters trong này thì lần bấm sắp xếp đầu tiên sẽ gửi đi tham số cũ.
  const fetchAssets = async (
    page = 1,
    perPage = 10,
    search = "",
    orgId = "",
    status = "",
    sort = filters
  ) => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: perPage,
        search: search.trim(),
        orgId: orgId,
        status,
        sort_by: sort.sort_by,
        sort_order: sort.sort_order,
      };

      const response = await assetAPIWithErrorHandling.getAssets(params);
      const { assets: customerData, pagination: paginationData } =
        assetHelpers.extractAssetList(response);

      setAssets(customerData);
      setPagination({
        current: paginationData.current,
        pageSize: paginationData.pageSize,
        total: paginationData.total,
        from: paginationData.from,
        to: paginationData.to,
        lastPage: paginationData.lastPage,
      });
    } catch (error) {
      message.error(error.message || "Không thể tải danh sách tài sản");
      console.error("Error fetching assets:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadOrganizations();
    fetchAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeOrg = (newValue) => {
    setValueOrg(newValue);
    fetchAssets(1, pagination.pageSize, searchText, newValue, filters.status);
  };

  // Handle table pagination change
  const handleTableChange = (paginationConfig, tableFilters, sorter) => {
    // Bấm lần thứ ba trên đầu cột là antd bỏ sắp xếp: sorter.order về undefined.
    // Khi đó quay lại thứ tự mặc định thay vì giữ nguyên cột vừa bỏ.
    const nextSort = sorter?.order
      ? {
          sort_by: sorter.field,
          sort_order: sorter.order === "descend" ? "desc" : "asc",
        }
      : DEFAULT_SORT;

    let statusFilter = "";
    if (tableFilters && tableFilters.status && tableFilters.status.length > 0) {
      statusFilter = tableFilters.status[0] ? "active" : "inactive";
    }

    setFilters((prev) => ({ ...prev, ...nextSort }));

    // Đổi trang / lọc thì bỏ tích chọn, tránh xoá bản ghi không còn thấy trên màn
    setSelectedRowKeys([]);

    fetchAssets(
      paginationConfig.current,
      paginationConfig.pageSize,
      searchText,
      valueOrg,
      statusFilter,
      nextSort
    );
  };

  // Handle search
  const handleSearch = (value) => {
    setSearchText(value);
    fetchAssets(1, pagination.pageSize, value, valueOrg, filters.status);
  };

  // Xoá toàn bộ bộ lọc
  const handleClearSearch = () => {
    setSearchText("");
    setValueOrg(undefined);
    fetchAssets(1, pagination.pageSize, "", "", filters.status);
  };

  // Action handlers
  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingRecord(null);
  };

  const handleInfo = (record) => {
    setDetailId(record.id);
    setDetailOpen(true);
  };

  // Từ drawer chi tiết bấm "Chỉnh sửa" -> đóng chi tiết, mở form ngay
  const handleEditFromDetail = (asset) => {
    setDetailOpen(false);
    setEditingRecord(asset);
    setFormOpen(true);
  };

  const handleDelete = async (record) => {
    try {
      const confirmed = await confirmDelete({ entity: "tài sản", name: record.name });
      if (!confirmed) return;

      await assetAPIWithErrorHandling.deleteAsset(record.id);
      message.success("Xóa tài sản thành công");

      fetchAssets(
        pagination.current,
        pagination.pageSize,
        searchText,
        valueOrg,
        filters.status
      );
    } catch (error) {
      message.error(error.message || "Không thể xóa tài sản");
    }
  };

  const handleBulkDelete = async () => {
    const count = selectedRowKeys.length;
    if (count === 0) return;

    const confirmed = await confirmAction({
      title: `Xóa ${count} tài sản đã chọn?`,
      content: "Tài sản đã phát sinh khoản thu sẽ không xóa được.",
      okText: "Xóa",
      okType: "danger",
    });
    if (!confirmed) return;

    setBulkDeleting(true);
    try {
      const res = await assetAPIWithErrorHandling.bulkDeleteAssets(selectedRowKeys);
      const result = res?.data?.data;

      if (result?.failed > 0) {
        // Nếu các bản ghi cùng một lý do thì nói thẳng lý do, đỡ phải xoá lẻ để dò
        const reasons = [...new Set((result.errors || []).map((e) => e.message))];
        message.warning({
          content:
            reasons.length === 1
              ? `Đã xóa ${result.success} tài sản. ${reasons[0]}`
              : `Đã xóa ${result.success} tài sản, ${result.failed} tài sản không xóa được do đang có khoản thu hoặc không thuộc quyền của bạn.`,
          duration: 8,
        });
      } else {
        message.success(res?.data?.message || `Đã xóa ${count} tài sản`);
      }

      setSelectedRowKeys([]);

      // Xoá hết bản ghi của trang hiện tại thì lùi về trang trước
      const removed = result?.success ?? count;
      const isPageEmptied = assets.length === removed && pagination.current > 1;
      fetchAssets(
        isPageEmptied ? pagination.current - 1 : pagination.current,
        pagination.pageSize,
        searchText,
        valueOrg,
        filters.status
      );
    } catch (error) {
      message.error(error.message || "Không thể xóa các tài sản đã chọn");
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleAdd = () => {
    setEditingRecord(null);
    setFormOpen(true);
  };

  // ===== Định nghĩa cột =====
  const columnDefs = [
    {
      key: "index", label: "STT", fixed: true,
      column: {
        title: "STT", key: "index", width: 60,
        render: (_, __, index) =>
          (pagination.current - 1) * pagination.pageSize + index + 1,
      },
    },
    {
      key: "name", label: "Tên tài sản", fixed: true,
      column: {
        title: "Tên tài sản", dataIndex: "name", key: "name", width: 200, fixed: "left",
        // Sắp xếp ở backend (cả danh sách), không phải chỉ trang đang xem.
        sorter: true,
        sortOrder:
          filters.sort_by === "name"
            ? filters.sort_order === "desc"
              ? "descend"
              : "ascend"
            : null,
      },
    },
    {
      key: "organization_name", label: "Đơn vị",
      column: {
        title: "Đơn vị", dataIndex: "organization_name", key: "organization_name", width: 200,
        render: (_, record) => (
          <Tag color="geekblue">{record?.organization?.name}</Tag>
        ),
      },
    },
    {
      key: "customer_name", label: "Khách hàng",
      column: {
        title: "Khách hàng", dataIndex: "customer_name", key: "customer_name", width: 200,
        render: (_, record) => {
          const customer = record?.customer;

          if (!customer) return "-";

          return (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <div className="font-medium">{customer.name}</div>
                <Text type="secondary" className="text-xs">
                  {customer.phone}
                </Text>
              </div>

              <Button
                type="text"
                size="small"
                icon={<InfoCircleOutlined />}
                style={{ color: "#0070D7", padding: 4, height: "auto" }}
                onClick={() => navigate(`/customers/information/${customer.id}`)}
              />
            </div>
          );
        },
      },
    },
    {
      key: "description", label: "Chi tiết",
      column: {
        title: "Chi tiết", dataIndex: "description", key: "description",
        ellipsis: true, width: 200,
        render: (text) => text || "-",
      },
    },
    {
      key: "representative_name", label: "Chủ hộ",
      column: {
        title: "Chủ hộ", dataIndex: "representative_name", key: "representative_name", width: 200,
        render: (text) => text || "-",
      },
    },
    {
      key: "representative_phone", label: "SĐT chủ hộ",
      column: {
        title: "SĐT chủ hộ", dataIndex: "representative_phone", key: "representative_phone", width: 200,
        render: (text) => text || "-",
      },
    },
    {
      key: "street", label: "Đường",
      column: {
        title: "Đường", dataIndex: "street", key: "street", width: 200,
        render: (text) => text || "-",
      },
    },
    {
      key: "status", label: "Trạng thái",
      column: {
        title: "Trạng thái", dataIndex: "status", key: "status", width: 200,
        render: (status) => (
          <Tag color={status ? "green" : "red"}>
            {status ? "Hoạt động" : "Không hoạt động"}
          </Tag>
        ),
        filters: [
          { text: "Hoạt động", value: true },
          { text: "Không hoạt động", value: false },
        ],
        filterMultiple: false,
      },
    },
    actionColumnDef((record) => [
      { key: "info", label: "Xem chi tiết", icon: <InfoCircleOutlined />, tone: "view", onClick: () => handleInfo(record) },
      { key: "edit", label: "Chỉnh sửa", icon: <EditOutlined />, tone: "edit", onClick: () => handleEdit(record) },
      { key: "delete", label: "Xóa", icon: <DeleteOutlined />, tone: "danger", onClick: () => handleDelete(record) },
    ]),
  ];

  // ===== Định nghĩa bộ lọc =====
  const filterDefs = [
    {
      key: "search", label: "Tìm kiếm", span: { xs: 24, sm: 12, md: 8 },
      field: (
        <Input.Search
          placeholder="Tìm theo tài sản, mã, khách hàng, chủ hộ, SĐT..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onSearch={handleSearch}
          onClear={handleClearSearch}
          enterButton={<SearchOutlined />}
          allowClear
        />
      ),
    },
    {
      key: "organization", label: "Đơn vị", span: { xs: 24, sm: 12, md: 8 },
      field: (
        <TreeSelect
          treeDataSimpleMode
          style={{ width: "100%" }}
          value={valueOrg}
          styles={{ popup: { root: { maxHeight: 400, overflow: "auto" } } }}
          placeholder="Chọn đơn vị"
          onChange={onChangeOrg}
          treeData={treeData}
          allowClear
          showSearch
          filterTreeNode={(input, item) =>
            item.title.toLowerCase().includes(input.toLowerCase())
          }
        />
      ),
    },
  ];

  const columns = selectVisibleColumns(columnDefs, visibleColumns);
  const hasActiveFilter = !!(searchText || valueOrg);

  // File xuất ra phải khớp đúng những gì đang thấy trên màn, nên dùng cùng bộ tham số
  // với fetchAssets — kể cả `status` rỗng, vì backend hiểu "có gửi status" là lọc tài
  // sản đang hoạt động; bỏ đi là file lại có thêm cả tài sản đã ngừng.
  const exportParams = {
    search: searchText.trim(),
    orgId: valueOrg ?? "",
    status: filters.status,
    sort_by: filters.sort_by,
    sort_order: filters.sort_order,
  };

  return (
    <BrandedPageLayout
      title="Tài sản"
      subtitle="Quản lý danh sách tài sản theo đơn vị và khách hàng."
      breadcrumbItems={[{ title: "Tài sản" }, { title: "Danh sách" }]}
    >
      <ListToolbar
        filterDefs={filterDefs}
        visibleFilters={visibleFilters}
        onChangeVisibleFilters={setVisibleFilters}
        columnDefs={columnDefs}
        visibleColumns={visibleColumns}
        onChangeVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
        hasActiveFilter={hasActiveFilter}
        onClearFilter={handleClearSearch}
        onReload={() => fetchAssets(pagination.current, pagination.pageSize, searchText, valueOrg, filters.status)}
        extra={
          <Space size="small">
            {selectedRowKeys.length > 0 && (
              <>
                <Text type="secondary">Đã chọn {selectedRowKeys.length}</Text>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  loading={bulkDeleting}
                  onClick={handleBulkDelete}
                >
                  Xóa đã chọn
                </Button>
              </>
            )}
            <ExportExcelButton
              url="/api/v1/assets/export"
              params={exportParams}
              fileName="danh_sach_tai_san.xlsx"
              actions={["assets.index"]}
              type="default"
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Thêm tài sản
            </Button>
          </Space>
        }
      />

      <ListTableCard
        columns={columns}
        dataSource={assets}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
        }}
        onChange={handleTableChange}
        emptyText="Không có dữ liệu"
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
          preserveSelectedRowKeys: false,
        }}
      />

      <AssetFormDrawer
        open={formOpen}
        record={editingRecord}
        treeData={treeData}
        onClose={handleCloseForm}
        onSaved={() =>
          // Sửa thì ở nguyên trang đang xem, thêm mới thì về trang 1 để thấy bản ghi vừa tạo
          fetchAssets(
            editingRecord ? pagination.current : 1,
            pagination.pageSize,
            searchText,
            valueOrg,
            filters.status
          )
        }
      />

      <AssetDetailDrawer
        open={detailOpen}
        assetId={detailId}
        onClose={() => setDetailOpen(false)}
        onEdit={handleEditFromDetail}
      />
    </BrandedPageLayout>
  );
}
